import React, { useMemo } from 'react';
import moment from 'moment';
import { Placement } from '@floating-ui/react';
import KubitTooltip from '../KubitTooltip';
import { bootstrapTimezoneSelector } from '../../redux/selectors/bootstrap';
import { useAppSelector } from '../../redux/hooks';
import { Maybe, TimeFormat } from '../../utils/apollo/graphqlSchema';
import {
  DisplayDateFormat,
  globalFormat,
  isDateValid,
} from '../../utils/formatting';
import { GraphQLDate } from '../../utils/types';
import { MaybeNil } from '../../utils/interfaces';
import { isNil } from 'lodash';

interface IProps {
  placement?: Placement;
  disabled?: boolean;
  timestamp: MaybeNil<GraphQLDate>;
  dateFormat: DisplayDateFormat;
  timeFormat?: TimeFormat;
  showTimezone?: boolean; // if true, the timestamp + timezone will show on the tooltip
  className?: string;
  /**
   * if timezone is not null, we need to convert timestamp to the timezone date.
   * case: schedules use user's timezone, but lastRunAt is utc timezone
   */
  timezone?: Maybe<string>;
}

const TimezonePopup = ({
  timestamp,
  dateFormat,
  timeFormat,
  placement = 'top-start',
  showTimezone,
  className,
  timezone,
  disabled,
}: IProps) => {
  const syndicateTimezone = useAppSelector(bootstrapTimezoneSelector);
  const displayedTimezone = timezone || syndicateTimezone;
  const [label, tooltipContent] = useMemo(() => {
    if (isNil(timestamp)) {
      return ['N/A', 'N/A'];
    }

    const isTimestampValid = isDateValid(timestamp);

    if (!isTimestampValid) {
      /*
        Sometimes, instead of common date, timestamp can be
        a random string like '5/30/2022 - Android'.
        In this case we are going to show it as is
      */
      return [timestamp, timestamp];
    }

    const formattedDate = globalFormat.formatDate(timestamp, {
      dateFormat,
      timeFormat,
    });

    if (!showTimezone) {
      return [formattedDate, formattedDate];
    }

    const shortTz = moment().tz(displayedTimezone).zoneAbbr();

    return [formattedDate, `${formattedDate} ${shortTz}`];
  }, [dateFormat, timeFormat, showTimezone, timestamp, displayedTimezone]);

  return (
    <KubitTooltip
      content={tooltipContent}
      placement={placement}
      disabled={disabled}
      noOpenDelay={true}
    >
      <span className={className}>{label}</span>
    </KubitTooltip>
  );
};

export default TimezonePopup;
